import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';
import { useSnapshot } from 'valtio';

import * as authStore from '@cartwhl/common/stores/authStore';
import * as userStore from '@cartwhl/common/stores/userStore';
import Avatar from '@cartwhl/ui/atoms/Avatar';
import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';

import NavigationLink from '../NavigationLink';
import Wordmark from '../Wordmark';
import { pageFrame } from '../page-frame';
import useAppletsUrl from '../../hooks/useAppletsUrl';
import DesktopMenu from './components/DesktopMenu';
import {
  learnItems,
  modelsItems,
  pricingItems,
  productMenuSections,
} from './content';

type UserMenuComponent = typeof import('@cartwhl/ui/organisms/UserMenu').default;
type MobileMenuComponent = typeof import('./components/MobileMenu').default;

let userMenuImport: Promise<{ default: UserMenuComponent }> | undefined;
let mobileMenuImport: Promise<{ default: MobileMenuComponent }> | undefined;

function subscribeToHydration() {
  return () => {};
}

function useHasHydrated() {
  return useSyncExternalStore(subscribeToHydration, () => true, () => false);
}

function importUserMenu() {
  userMenuImport ??= import('@cartwhl/ui/organisms/UserMenu').catch((error) => {
    userMenuImport = undefined;
    throw error;
  });
  return userMenuImport;
}

function importMobileMenu() {
  mobileMenuImport ??= import('./components/MobileMenu').catch((error) => {
    mobileMenuImport = undefined;
    throw error;
  });
  return mobileMenuImport;
}

export default function Header() {
  const { authStatus } = useSnapshot(authStore.state);
  const { userInfo } = useSnapshot(userStore.state);
  const hasHydrated = useHasHydrated();
  const [openDesktopMenu, setOpenDesktopMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [MobileMenu, setMobileMenu] = useState<MobileMenuComponent | null>(null);
  const [UserMenu, setUserMenu] = useState<UserMenuComponent | null>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuError, setMobileMenuError] = useState(false);
  const [userMenuError, setUserMenuError] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const isSignedIn = authStatus === 'user';
  const appletsUrl = useAppletsUrl();
  const { pathname } = useLocation();
  const pricingCtaHref =
    pathname === '/pricing/enterprise-plans'
      ? '/pricing#pricing-cards'
      : pathname === '/pricing' || pathname.startsWith('/pricing/')
        ? `${pathname}#pricing-cards`
        : '/pricing';

  useEffect(() => {
    function closeOnDesktop() {
      if (window.matchMedia('(min-width: 1024px)').matches)
        setMobileOpen(false);
    }
    window.addEventListener('resize', closeOnDesktop);
    return () => window.removeEventListener('resize', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!mobileOpen || MobileMenu) return;
    function closePendingMenu(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      setMobileOpen(false);
      menuButtonRef.current?.focus();
    }
    document.addEventListener('keydown', closePendingMenu);
    return () => document.removeEventListener('keydown', closePendingMenu);
  }, [mobileOpen, MobileMenu]);

  useEffect(() => {
    if (!hasHydrated || !isSignedIn || UserMenu) return;
    void loadUserMenu();
  }, [hasHydrated, isSignedIn, UserMenu]);

  function setDesktopMenu(menu: string, open: boolean) {
    setOpenDesktopMenu((currentMenu) => {
      if (open) return menu;
      return currentMenu === menu ? null : currentMenu;
    });
  }

  function toggleMobileMenu() {
    if (mobileOpen) {
      setMobileOpen(false);
      menuButtonRef.current?.focus();
      return;
    }
    // Keep focus on the trigger until the deferred menu mounts and moves it to
    // its close button. The pending state is deliberately not a modal.
    setMobileOpen(true);
    void loadMobileMenu();
  }

  function loadMobileMenu() {
    setMobileMenuError(false);
    return importMobileMenu()
      .then(({ default: MobileMenuComponent }) => {
        setMobileMenu(() => MobileMenuComponent);
      })
      .catch(() => {
        setMobileMenuError(true);
      });
  }

  function loadUserMenu() {
    setUserMenuError(false);
    return importUserMenu()
      .then(({ default: UserMenuComponent }) => {
        setUserMenu(() => UserMenuComponent);
      })
      .catch(() => {
        setUserMenuError(true);
      });
  }

  function openUserMenu() {
    setUserMenuOpen(true);
    void loadUserMenu();
  }

  return (
    <header
      className={`bg-primary border-primary h-16 sticky top-0 z-50 shrink-0 border-b min-[1080px]:border-b-0 xl:-top-4 xl:h-20 2xl:-top-16 2xl:h-32 2xl:border-b-0 ${pageFrame}`}
    >
      {/* The shell scrolls away at xl and above; the inner row stays pinned at 64px.
          Keep spacing in flow instead of changing height to avoid scroll jumps. */}
      <div aria-hidden="true" className="hidden xl:block xl:h-2 2xl:h-8" />
      <div className="sticky top-0 flex h-16 items-center justify-between px-4 sm:px-12">
        <Wordmark />
        <div className="hidden items-center gap-6 lg:flex">
          <nav
            aria-label="Primary navigation"
            className="flex items-center gap-6"
          >
            <DesktopMenu
              label="Models"
              items={modelsItems}
              open={openDesktopMenu === 'Models'}
              onOpenChange={(open) => setDesktopMenu('Models', open)}
            />
            <DesktopMenu
              label="Products"
              sections={productMenuSections}
              open={openDesktopMenu === 'Products'}
              onOpenChange={(open) => setDesktopMenu('Products', open)}
            />
            <NavigationLink
              href="/research"
              className="text-primary hover:text-secondary"
            >
              <Text token="16/medium">Research</Text>
            </NavigationLink>
            <DesktopMenu
              label="Pricing"
              items={pricingItems}
              open={openDesktopMenu === 'Pricing'}
              onOpenChange={(open) => setDesktopMenu('Pricing', open)}
            />
            <NavigationLink
              href="/company"
              className="text-primary hover:text-secondary"
            >
              <Text token="16/medium">Company</Text>
            </NavigationLink>
            <DesktopMenu
              label="Learn"
              items={learnItems}
              open={openDesktopMenu === 'Learn'}
              onOpenChange={(open) => setDesktopMenu('Learn', open)}
            />
          </nav>
          <div className="flex min-w-[208px] items-center justify-end gap-2">
            {!hasHydrated ? (
              <div aria-hidden="true" className="flex items-center gap-2">
                <div className="bg-secondary h-9 w-[76px] rounded-md" />
                <div className="bg-secondary h-9 w-[124px] rounded-md" />
              </div>
            ) : isSignedIn ? (
              <>
                <Button as="a" href={appletsUrl} variant="cta">
                  Launch Cartwheel
                </Button>
                {UserMenu ? (
                  <UserMenu
                    open={userMenuOpen}
                    onOpenChange={setUserMenuOpen}
                  />
                ) : (
                  <button
                    type="button"
                    aria-label={
                      userMenuError
                        ? 'Retry loading account menu'
                        : 'Open account menu'
                    }
                    aria-busy={!userMenuError}
                    onClick={openUserMenu}
                    className="hover:bg-gray-15 data-[state=open]:bg-gray-15 hover:dark:bg-gray-75 data-[state=open]:dark:bg-gray-90 outline-hidden flex h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors"
                  >
                    <Avatar userInfo={userInfo} />
                    <span className="sr-only">
                      {userMenuError
                        ? 'Account menu failed to load. Activate to retry.'
                        : 'Loading account menu…'}
                    </span>
                  </button>
                )}
              </>
            ) : (
              <>
                <Button as={NavigationLink} href="/login" variant="secondary">
                  Sign in
                </Button>
                <Button as={NavigationLink} href={pricingCtaHref} variant="cta">
                  Start free trial
                </Button>
              </>
            )}
          </div>
        </div>
        <button
          ref={menuButtonRef}
          type="button"
          className="text-primary -mr-4 inline-flex h-10 items-center px-4 lg:hidden"
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation"
          onClick={toggleMobileMenu}
          onFocus={() => void loadMobileMenu()}
          onPointerEnter={() => void loadMobileMenu()}
          onTouchStart={() => void loadMobileMenu()}
        >
          <Text token="16/medium">Menu</Text>
        </button>
      </div>
      {MobileMenu ? (
        <MobileMenu
          open={mobileOpen}
          onOpenChange={setMobileOpen}
          triggerRef={menuButtonRef}
          pricingCtaHref={pricingCtaHref}
        />
      ) : mobileOpen ? (
        <div
          className="bg-primary border-primary fixed inset-x-0 top-16 z-50 flex items-center gap-3 border-b px-4 py-3 lg:hidden"
          role={mobileMenuError ? 'alert' : 'status'}
          aria-live="polite"
        >
          <Text token="14/medium">
            {mobileMenuError
              ? 'Navigation could not load.'
              : 'Loading navigation…'}
          </Text>
          {mobileMenuError && (
            <button
              type="button"
              className="text-primary hover:text-secondary rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => void loadMobileMenu()}
            >
              Retry
            </button>
          )}
          <button
            type="button"
            className="text-primary hover:text-secondary ml-auto rounded-md underline focus-visible:outline-2 focus-visible:outline-offset-2"
            onClick={toggleMobileMenu}
          >
            Cancel
          </button>
        </div>
      ) : null}
    </header>
  );
}
